import { ref, watch, onUnmounted, type CSSProperties, type Ref } from 'vue'

const VIEWPORT_PADDING = 16
const TRIGGER_GAP = 4
const DEFAULT_MAX_HEIGHT = 240

/**
 * Positions a dropdown panel that is teleported to <body>, keeping it
 * anchored to its trigger element. Because the panel is fixed-positioned
 * at body level, no ancestor overflow container (e.g. a scrolling modal
 * body) can clip it. The panel opens above the trigger when there is not
 * enough room below, and repositions while scrolling or resizing.
 */
export function useDropdownPosition(options: {
  isOpen: Ref<boolean>
  triggerRef: Ref<HTMLElement | null>
  /** Cap on the panel height in px; the panel shrinks to fit the viewport */
  maxHeight?: number
  /** Fixed panel width in px; defaults to the trigger's width */
  width?: number
}) {
  const panelStyle = ref<CSSProperties>({})

  const updatePosition = () => {
    const trigger = options.triggerRef.value
    if (!trigger || !options.isOpen.value) {
      return
    }

    const rect = trigger.getBoundingClientRect()
    const desiredHeight = options.maxHeight ?? DEFAULT_MAX_HEIGHT
    const width = Math.min(
      options.width ?? rect.width,
      window.innerWidth - VIEWPORT_PADDING * 2
    )

    const spaceBelow =
      window.innerHeight - rect.bottom - TRIGGER_GAP - VIEWPORT_PADDING
    const spaceAbove = rect.top - TRIGGER_GAP - VIEWPORT_PADDING

    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(rect.left, window.innerWidth - width - VIEWPORT_PADDING)
    )

    const style: CSSProperties = {
      position: 'fixed',
      left: `${left}px`,
      width: `${width}px`,
      overflowY: 'auto',
    }

    if (spaceBelow >= desiredHeight || spaceBelow >= spaceAbove) {
      style.top = `${rect.bottom + TRIGGER_GAP}px`
      style.maxHeight = `${Math.max(0, Math.min(desiredHeight, spaceBelow))}px`
    } else {
      // Anchor by bottom so a panel shorter than maxHeight still sits
      // flush against the trigger instead of floating above it
      style.bottom = `${window.innerHeight - rect.top + TRIGGER_GAP}px`
      style.maxHeight = `${Math.max(0, Math.min(desiredHeight, spaceAbove))}px`
    }

    panelStyle.value = style
  }

  // Capture phase so scrolls inside nested containers (e.g. a modal body)
  // reposition the panel too, not just window scrolls
  const addListeners = () => {
    window.addEventListener('resize', updatePosition, { passive: true })
    document.addEventListener('scroll', updatePosition, {
      capture: true,
      passive: true,
    })
  }

  const removeListeners = () => {
    window.removeEventListener('resize', updatePosition)
    document.removeEventListener('scroll', updatePosition, { capture: true })
  }

  watch(options.isOpen, (isOpen) => {
    if (isOpen) {
      updatePosition()
      addListeners()
    } else {
      removeListeners()
    }
  })

  onUnmounted(removeListeners)

  return { panelStyle, updatePosition }
}
